import { toast } from "react-toastify";
import { fetchOnboardingDetails } from "../Api/Onboarding";
import { determineOnboardingFlow, getFlowDecisionReason, trackFlowDecision } from "./onboardingFlowDecision";
import { consumePendingDeepLink } from "./postAuthRedirect";
import { consumeFocusRedirect } from "./focusRedirect";

/**
 * Role groups for redirection.
 */
const roles = {
  adminListDefaultRedirectedUser: [
    "Super Admin - Admin", 
    "Tech Admin - Admin", 
    "Tech Admin - Member"
  ],
  dashboardDefaultRedirectedUser: [
    "Consultant - Admin", 
    "Consultant - Member"
  ],
  onBoardedUserRedirection: [
    "User - Admin", 
    "User - Management", 
    "User - Vendor"
  ],
  onBoardedUserRedirectionPrevention: [
    "User - Member",
    "User - Guest",
    "Auditor - Admin",
    "Auditor - Member",
    "Regulator - Admin",
    "Regulator - Member"
  ]
};

/**
 * Check if user role is in a specific role group.
 * @param {Array} roleGroup - Array of roles to check against.
 * @param {string} userRole - Current user's role.
 * @returns {boolean} - Returns true if user role is in the role group.
 */
const checkRole = (roleGroup, userRole) => roleGroup.includes(userRole);

/** Route for each onboarding flow value ('full' | 'simplified' | 'brand'). */
const ONBOARDING_FLOW_ROUTES = {
  simplified: "/bizdetails",
  brand: "/onboarding/brand",
  full: "/onboarding",
};

const navigateToFlow = (navigate, flow, context) => {
  const route = ONBOARDING_FLOW_ROUTES[flow] || ONBOARDING_FLOW_ROUTES.full;
  console.log(`🧭 ${context}: Redirecting to ${flow} flow: ${route}`);
  navigate(route);
};

/**
 * Handles redirection based on onboarding, role data, and source-based routing.
 * @param {Object} navigate - useNavigate hook from react-router-dom.
 * @param {Object} userdata - User data object from localStorage.
 * @param {string} orgID - Organization ID from localStorage.
 * @param {boolean} invitationCodeStore - Legacy invitation code state (still supported for backward compatibility).
 */
export const handleRedirection = async (navigate, userdata, orgID, invitationCodeStore = false) => {
  // A "Complete Now" email deep link (see Layout.js) stashed the assignment
  // route before bouncing through Login or invitation signup - restore it
  // here instead of the normal role-based landing page, for both callers
  // (LoginModule.js post-login and TwoFactorModule post-invitation-signup).
  const pendingDeepLink = consumePendingDeepLink();
  if (pendingDeepLink) {
    navigate(pendingDeepLink, { replace: true });
    return;
  }
  // A Focus page or chat session the user asked for before signing in (BUG-11); one-shot.
  const focusRedirect = consumeFocusRedirect();
  if (focusRedirect) {
    localStorage.setItem("mode", "Focus Mode");
    navigate(focusRedirect, { replace: true });
    return;
  }

  try {
    const onboardingData = await fetchOnboardingDetails(orgID);
    // Normalize role access: callers pass either the full response.user object (Role) or nested user.role
    const userRole = (
      userdata?.Role ||
      userdata?.role ||
      userdata?.user?.Role ||
      userdata?.user?.role ||
      localStorage.getItem('userType') ||
      null
    );
    const isOnboardingFinished = onboardingData?.data?.onboarding_step === "finish";

    localStorage.setItem("onboardingDetails", JSON.stringify(onboardingData));
    localStorage.setItem("loggedin", true);

    /* User redirection based on role and onboarding status */
    if (checkRole(roles.adminListDefaultRedirectedUser, userRole) || checkRole(roles.dashboardDefaultRedirectedUser, userRole)) {
      const lastMode = localStorage.getItem("mode");
      if (lastMode === "Pro Mode") {
        navigate(`/adminlist?view=Dashboard`, { replace: true });
        localStorage.setItem("mode", "Pro Mode");
      } else {
        navigate(`/view/focus`, { replace: true });
        localStorage.setItem("mode", "Focus Mode");
      }
    } else if (checkRole(roles.onBoardedUserRedirection, userRole)) {
      if (isOnboardingFinished) {
        // User has completed onboarding - go to dashboard
        navigate(`/view/focus`, { replace: true });
        localStorage.setItem("mode", "Focus Mode");
      } else if (invitationCodeStore) {
        // 🆕 Invitation users: Apply source-based routing but with invitation context
        console.log('👥 Invitation user detected - applying source-based routing');
        
        const signupData = JSON.parse(localStorage.getItem("signupData") || "{}");
        const flow = determineOnboardingFlow(signupData);
        const reason = getFlowDecisionReason(signupData);
        
        console.log('🎯 Invitation User Flow Decision:', {
          flow,
          reason,
          invitationCodeStore: true,
          signupData: {
            url_source: signupData.url_source,
            raw_url_source: signupData.raw_url_source,
            source: signupData.source,
            source_method: signupData.source_method
          }
        });
        
        // Track the decision with invitation context
        trackFlowDecision(flow, { ...signupData, invitation: true });

        navigateToFlow(navigate, flow, 'Invitation user');
      } else {
        // 🆕 Regular users: server-assigned flow first, then source-based decision.
        // The backend stamps signup_onboarding_flow on the organization at
        // registration, so routing survives device switches / cleared storage.
        const signupData = JSON.parse(localStorage.getItem("signupData") || "{}");
        const serverFlow = onboardingData?.data?.signup_onboarding_flow;
        const flow = ['full', 'simplified', 'brand'].includes(serverFlow)
          ? serverFlow
          : determineOnboardingFlow(signupData);
        const reason = ['full', 'simplified', 'brand'].includes(serverFlow)
          ? 'Server-assigned onboarding flow (organization record)'
          : getFlowDecisionReason(signupData);

        console.log('🎯 Regular User Flow Decision:', {
          flow,
          reason,
          server_flow: serverFlow || null,
          signupData: {
            url_source: signupData.url_source,
            raw_url_source: signupData.raw_url_source,
            source: signupData.source,
            source_method: signupData.source_method
          }
        });

        // Track the decision for analytics
        trackFlowDecision(flow, signupData);

        navigateToFlow(navigate, flow, 'Regular user');
      }
    } else if (checkRole(roles.onBoardedUserRedirectionPrevention, userRole)) {
      if (isOnboardingFinished) {
        navigate(`/view/focus`, { replace: true });
        localStorage.setItem("mode", "Focus Mode");
      } else {
        // Graceful handling: restricted users (Auditor, Regulator, User Member/Guest) cannot complete onboarding.
        // Always send to onboardingNotFinished so they see a clear message and admin contact instead of the Business Fact Sheet.
        console.log('Restricted user: Onboarding not complete - redirecting to onboardingNotFinished');
        navigate("/onboardingNotFinished", { replace: true });
      }
    } else {
      toast.error("User not found.");
    }
  } catch (error) {
    toast.error("Failed to retrieve onboarding details. Please try again.");
  }
};
